import { useCallback, useEffect, useState } from "react";
import { Mail, Send } from "lucide-react";
import { useAuthed } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const KIND = {
  order_confirmation: "Order confirmation", payment_receipt: "Payment & receipt", status_preparing: "Preparing",
  status_out_for_delivery: "Out for delivery", status_delivered: "Delivered", status_cancelled: "Cancelled",
};

export default function EmailSettings() {
  const call = useAuthed();
  const [info, setInfo] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const r = await call("get", "/admin/email/status"); setInfo(r.data); } catch { toast.error("Could not load email status"); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setSending(true);
    try { await call("post", "/admin/email/test", { to }); toast.success(`Test email sent to ${to}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Test email failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="font-display font-extrabold text-xl sm:text-2xl flex items-center gap-2"><Mail size={22} className="text-[#CC1A00]" /> Order emails</h1>
        <p className="text-sm text-gray-500 mt-1">Customers who enter an email at checkout get an order confirmation, a payment receipt and status updates (preparing, on the way, delivered, cancelled). These are service emails only, with no promotions.</p>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 text-sm" data-testid="email-status">
        {info ? (
          <>Sender: <b>{info.sender}</b> · Status: {info.configured
            ? <span className="text-emerald-700 font-semibold">Ready</span>
            : <span className="text-red-600 font-semibold">Mailbox password missing</span>}</>
        ) : "Loading…"}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="font-semibold text-sm mb-2">Send a test email</div>
        <div className="flex gap-2">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" data-testid="test-email-input" />
          <Button onClick={sendTest} disabled={sending || !to} className="mlobox-gradient text-white rounded-full" data-testid="test-email-send">
            <Send size={14} className="mr-1" /> {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
      {info?.recent?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Recent order emails</div>
            <button onClick={load} className="text-xs font-semibold text-[#CC1A00]" data-testid="email-log-refresh">Refresh</button>
          </div>
          <ul className="text-xs space-y-1" data-testid="email-log">
            {info.recent.map((r, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span>{KIND[r.kind] || r.kind} → {r.to}</span>
                <span className={r.status === "sent" ? "text-emerald-700" : "text-red-600"}>{r.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
